<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useUnderwritingStore } from '@/stores/underwriting';
import { useActuarialStore } from '@/stores/actuarial';
import { usePortfolioStore } from '@/stores/portfolio';
import { useGlobalStore } from '@/stores/global';
import AutoComplete from 'primevue/autocomplete';
import InputTextComponent from '@/components/InputTextComponent.vue';
import Calendar from 'primevue/calendar';
import moment from 'moment';
import { api } from '@/services/api';
import openErrorMsg from '@/components/openErrorMsg';
import reverseSearch from '@/utilities/reverseSearch';
import DialogComponent from '@/components/Dialog/DialogComponent.vue';
import JetButton from '@/components/JetButton.vue';
import SelectComponent from '@/components/SelectComponent.vue';
import OptionComponent from '@/components/OptionComponent.vue';

const underwritingStore = useUnderwritingStore();
const portfolioStore = usePortfolioStore();
const globalStore = useGlobalStore();

const parameters = computed(() => portfolioStore.parameters);
const currency = computed(() => globalStore.currency.currencyFormat.currency);
const dictionary = computed(() => portfolioStore.dictionary);
const autoCompleteSuggestion = ref([]);
const newBusinessModalData = ref({});
const totalTableData = computed(() => underwritingStore.totalTableData);
const valueArrayColumns = computed(() => underwritingStore.valueArrayColumns);
// Hierarchy, GWP, Commission, Dates, Loads, Elasticity
const isAllValidated = ref([0, 0, 0, 0, 0, 0]);

function title(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

const additionalInfomationNeeded = ref<string[]>([]);
const additionalInfomationNeededSelection = ref({});
const actuarialHierarchy = ref();
const additionalInfomationDialog = ref(false);
const configureEarningRatioDialog = ref(false);
function checkNewDictionary() {
  additionalInfomationNeeded.value = [];
  additionalInfomationNeededSelection.value = {};
  for (const hr of parameters.value.hierarchies) {
    if (reverseSearch(dictionary.value[hr], title(newBusinessModalData.value[hr])) == -1) {
      additionalInfomationNeeded.value.push(hr);
      additionalInfomationNeededSelection.value[hr] = { name: null, isNew: false };
    }
  }
}

const actuarialStore = useActuarialStore();

async function getActuarialChunkGrouping() {
  globalStore.setLoading(true);
  await api
    .get(import.meta.env.VITE_API_URL + 'actuarial/get-actuarial-chunk-grouping', {
      params: {
        bounce_id: portfolioStore.selectedBounceID,
      },
    })
    .then((res) => {
      globalStore.setLoading(false);
      actuarialHierarchy.value = res.data.data.variable.actuarial_hierarchy;
      if (portfolioStore.isMirrored) {
        actuarialHierarchy.value = actuarialStore.configChunkGroupNamesIfMirrored(actuarialHierarchy.value);
      }
      checkNewDictionary();
      if (additionalInfomationNeeded.value.length > 0) {
        additionalInfomationDialog.value = true;
      } else {
        underwritingStore.onAddNewBusiness(
          newBusinessModalData.value,
          additionalInfomationNeededSelection.value,
          earningRatio.value
        );
      }
    })
    .catch((err) => {
      openErrorMsg('Error!');
      console.log(err);
      globalStore.setLoading(false);
    });
}

function onConfirm() {
  if (earningRatio.value.reduce((x, y) => x + y, 0).toFixed(4) != '1.0000') {
    openErrorMsg('Total earning ratio should be 1');
    return;
  }

  getActuarialChunkGrouping();
}

function checkGWP() {
  const isCorrect = isFinite(newBusinessModalData.value['GWP']) && newBusinessModalData.value['GWP'] != '';

  if (isCorrect) {
    isAllValidated.value[1] = 1;
  } else {
    isAllValidated.value[1] = 0;
  }
}

function checkElasticity() {
  const isCorrect =
    isFinite(newBusinessModalData.value['ELASTICITY']) && newBusinessModalData.value['ELASTICITY'] != '';

  if (isCorrect) {
    isAllValidated.value[5] = 1;
  } else {
    isAllValidated.value[5] = 0;
  }
}

function checkClaimsNature() {
  const isAllCorrect = parameters.value['claims_nature'].every((e: string) => {
    return isFinite(newBusinessModalData.value[e]) && newBusinessModalData.value[e] != '';
  });

  if (isAllCorrect) {
    isAllValidated.value[4] = 1;
  } else {
    isAllValidated.value[4] = 0;
  }
}

const all_uw_dates = computed(() => portfolioStore.all_uw_dates);

function checkDates() {
  if (newBusinessModalData.value['start_date'] == null || newBusinessModalData.value['end_date'] == null) {
    isAllValidated.value[3] = 0;
    return;
  }

  let a = moment(newBusinessModalData.value['start_date']).isBefore(newBusinessModalData.value['end_date']);
  let b = moment(newBusinessModalData.value['start_date']).isBetween(
    moment(all_uw_dates.value['month'][0], 'MMM-YYYY').subtract(1, 'months'),
    moment(all_uw_dates.value['month'][all_uw_dates.value['month'].length - 1], 'MMM-YYYY').add(1, 'months')
  );
  let c = moment(newBusinessModalData.value['end_date']).isBetween(
    moment(all_uw_dates.value['month'][0], 'MMM-YYYY').subtract(1, 'months'),
    moment(all_uw_dates.value['month'][all_uw_dates.value['month'].length - 1], 'MMM-YYYY').add(1, 'months')
  );

  const isDateCorrect = [a, b, c].every((e: any) => e);

  if (isDateCorrect) {
    isAllValidated.value[3] = 1;
  } else {
    isAllValidated.value[3] = 0;
  }
}

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

async function fetchDynamicController() {
  globalStore.setLoading(true);
  let data = null;

  let post = {
    anyChange: false,
    bounce_id: portfolioStore.selectedBounceID,
    underwriting_month: moment(all_uw_dates.value['month'][0], 'MMM-YYYY').format('YYYY-MM-01'),
    selectedLineSize: portfolioStore.selectedLineSize,
    variable: 'Commission',
    filters: parameters.value.hierarchies.map((hierarchy) => {
      return { [hierarchy]: Object.keys(dictionary.value[hierarchy]) };
    }),
  };

  await api
    .post(import.meta.env.VITE_API_URL + 'underwriting/fetch-dynamic-controller', post)
    .then((res) => {
      data = res.data.data;
      globalStore.setLoading(false);
    })
    .catch(() => {
      // openErrorMsg("Error!")
      globalStore.setLoading(false);
      globalStore.onConnectionError();
    });

  return data;
}

const formattedClaimsNature = [
  'Attritional A-priori',
  ...parameters.value.claims_nature.slice(1).map((e: any) => {
    return toTitleCase(e) + ' Load';
  }),
];
const formattedCommission = [
  ...parameters.value.commission.map((e: any) => {
    return e.split('_')[1] + ' Commission';
  }),
];

onMounted(async () => {
  newBusinessModalData.value = {};
  for (const i of parameters.value.hierarchies) {
    newBusinessModalData.value[i] = '';
  }

  const commissionData: any = await fetchDynamicController();

  newBusinessModalData.value['GWP'] = totalTableData.value['value_array'][0][valueArrayColumns.value['GWP']].toFixed(1);

  for (const i in parameters.value.claims_nature) {
    const name = parameters.value.claims_nature[i];
    const formattedName = formattedClaimsNature[i];
    newBusinessModalData.value[name] = (
      totalTableData.value['value_array'][0][valueArrayColumns.value[formattedName]] * 100
    ).toFixed(1);
  }

  newBusinessModalData.value['ELASTICITY'] =
    totalTableData.value['value_array'][0][valueArrayColumns.value['Elasticity']].toFixed(2);

  for (const i of parameters.value.commission) {
    if (commissionData) {
      newBusinessModalData.value[i + ' Commission'] = (
        commissionData.value[commissionData.variable.findIndex((e) => e == i)] * 100
      ).toFixed(1);
    } else {
      newBusinessModalData.value[i + ' Commission'] = 0;
    }
  }

  isAllValidated.value = [0, 1, 1, 0, 1, 1];

  newBusinessModalData.value['start_date'] = moment(all_uw_dates.value['month'][0], 'MMM-YYYY').toDate();
  newBusinessModalData.value['end_date'] = moment(
    all_uw_dates.value['month'][all_uw_dates.value['month'].length - 1],
    'MMM-YYYY'
  ).toDate();
  checkDates();
});

function checkCommission() {
  let sum = 0;
  let isAllCorrect = parameters.value['commission'].every((e: string) => {
    if (
      !isFinite(newBusinessModalData.value[e + ' Commission']) &&
      newBusinessModalData.value[e + ' Commission'] != ''
    ) {
      return false;
    } else {
      sum += parseFloat(newBusinessModalData.value[e + ' Commission']);
      return true;
    }
  });

  if (sum > 100) {
    isAllCorrect = false;
  }

  if (isAllCorrect) {
    isAllValidated.value[2] = 1;
  } else {
    isAllValidated.value[2] = 0;
  }
}

function checkHierarchy() {
  const isAllCorrect = parameters.value['hierarchies'].every((e: string) => {
    return newBusinessModalData.value[e] != '';
  });

  if (isAllCorrect) {
    isAllValidated.value[0] = 1;
  } else {
    isAllValidated.value[0] = 0;
  }
}

function onUpdateNewBusinessHierarchy(e, hr: string) {
  newBusinessModalData.value[hr] = e.target.value;
  checkHierarchy();
}

async function autoCompleteFunction(e, i) {
  autoCompleteSuggestion.value = Object.values(dictionary.value[parameters.value['hierarchies'][i]]).filter((word) =>
    word.toLowerCase().includes(e.query.toLowerCase())
  );
}

function onCloseModal() {
  underwritingStore.onChangeIsNewBusinessModal(false);
}

function onResetAdditionalInfomationName(hierarchy) {
  additionalInfomationNeededSelection.value[hierarchy]['name'] = null;
}

function onSubmitAdditionalInfomation() {
  // check if empty selection exists
  for (const hierarchy of additionalInfomationNeeded.value) {
    if (!additionalInfomationNeededSelection.value[hierarchy]['name']) {
      openErrorMsg('Please fill in all the required fields');
      return;
    }

    if (
      additionalInfomationNeededSelection.value[hierarchy]['isNew'] &&
      actuarialHierarchy.value[hierarchy].groupNames.includes(
        additionalInfomationNeededSelection.value[hierarchy]['name']
      )
    ) {
      openErrorMsg('Duplicated name');
      return;
    }
  }

  underwritingStore.onAddNewBusiness(
    newBusinessModalData.value,
    additionalInfomationNeededSelection.value,
    earningRatio.value
  );
}

const earningRatio = ref([1]);
const numberOfEarningRatio = ref(1);

function handleChange() {
  const ratio = 1 / numberOfEarningRatio.value;
  earningRatio.value = Array(numberOfEarningRatio.value).fill(ratio);
}
</script>
<template>
  <DialogComponent
    v-model="underwritingStore.isNewBusinessModal"
    title="Add new business"
    dialog-style="height: 700px; width: 950px; min-width: fit-content; background-color: rgb(243, 244, 246); overflow: auto;"
    @update:visible="onCloseModal"
  >
    <!-- EARNING RATIO -->
    <DialogComponent v-model="configureEarningRatioDialog" title="Tweak Earning Ratio">
      <div>
        <h1>Number Of Earning Ratios</h1>
        <el-input-number v-model="numberOfEarningRatio" :min="1" class="mb-6" @change="handleChange" />
        <div v-for="idx in earningRatio.length" :key="idx" class="mb-3">
          <h1 class="mb-2">Earning Ratio {{ idx }}:</h1>
          <el-input-number v-model="earningRatio[idx - 1]" :min="0" :step="0.001" :precision="4" />
        </div>
        <h1 class="text-red-500">
          *Note that the total ratio need to be 1. Current Total:
          {{ earningRatio.reduce((x, y) => x + y, 0).toFixed(4) }}
        </h1>
      </div>
      <template #footer>
        <el-button type="primary" @click="configureEarningRatioDialog = false">Confirm</el-button>
      </template>
    </DialogComponent>
    <!-- ADDITIONAL INFORMATION DIALOG -->
    <DialogComponent
      v-model="additionalInfomationDialog"
      title="Additional Infomation needed"
      dialog-style="height: 700px; overflow: auto;"
    >
      <div v-for="hierarchy in additionalInfomationNeeded" :key="hierarchy">
        <h1>{{ hierarchy }}</h1>
        <div class="flex flex-col mb-10">
          <el-switch
            v-model="additionalInfomationNeededSelection[hierarchy]['isNew']"
            active-text="Create New Group"
            size="large"
            inactive-text="Use Existing Group"
            @change="onResetAdditionalInfomationName(hierarchy)"
          />
          <span v-if="!additionalInfomationNeededSelection[hierarchy]['isNew']">
            <SelectComponent v-model="additionalInfomationNeededSelection[hierarchy]['name']" class="w-full">
              <OptionComponent
                v-for="item in actuarialHierarchy[hierarchy].groupNames"
                :key="item"
                :label="item"
                :value="item"
              ></OptionComponent>
            </SelectComponent>
          </span>
          <span v-else>
            <el-input
              v-model="additionalInfomationNeededSelection[hierarchy]['name']"
              placeholder="Enter new group name"
            />
          </span>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button type="primary" @click="onSubmitAdditionalInfomation()">Add</el-button>
        </span>
      </template>
    </DialogComponent>
    <div v-if="newBusinessModalData">
      <div v-for="(hr, i) in parameters.hierarchies" :key="hr" class="w-full">
        <div>{{ hr }} <span class="italic text-sm text-red-700">*</span></div>
        <AutoComplete
          v-model="newBusinessModalData[hr]"
          :suggestions="autoCompleteSuggestion"
          @input="onUpdateNewBusinessHierarchy($event, hr)"
          @complete="autoCompleteFunction($event, i)"
        />
      </div>
      <div>
        <div>Monthly GWP <span class="italic text-sm text-red-700">*</span></div>
        <div class="p-inputgroup h-10 mb-3">
          <InputTextComponent
            v-model="newBusinessModalData['GWP']"
            :error="newBusinessModalData['GWP'] != '' && !isFinite(newBusinessModalData['GWP'])"
            class="radius-0"
            @input="checkGWP"
          >
            <template #side>
              <span
                class="inline-flex flex-none items-center justify-center w-12 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                >{{ currency }}</span
              >
            </template>
          </InputTextComponent>
        </div>
        <span v-if="newBusinessModalData['GWP'] != '' && !isFinite(newBusinessModalData['GWP'])" class="text-red-500"
          >Please enter a valid GWP value.</span
        >
      </div>
      <div v-for="com in parameters.commission" :key="com">
        <div>
          {{ com + ' Commission' }}
          <span class="italic text-sm text-red-700">*</span>
        </div>
        <div class="h-10 mb-3">
          <InputTextComponent
            v-model="newBusinessModalData[com + ' Commission']"
            @update:model-value="checkCommission()"
          >
            <template #side>
              <span
                class="inline-flex flex-none items-center justify-center w-12 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                >%</span
              >
            </template>
          </InputTextComponent>
        </div>
      </div>
      <div v-if="isAllValidated[2] == 0" class="my-5 w-full p-8 bg-red-200 text-red-700 flex flex-row">
        <div class="mr-2">
          <i class="pi pi-times-circle" style="font-size: 1.5rem"></i>
        </div>
        <div>Total commission should be between 0% and 100%</div>
      </div>
      <div class="flex flex-row mb-3">
        <div class="mr-2">
          <div>Start date <span class="italic text-sm text-red-700">*</span></div>
          <Calendar
            v-model="newBusinessModalData['start_date']"
            view="month"
            date-format="M-yy"
            class="h-10"
            :manual-input="false"
            @date-select="checkDates()"
          />
        </div>

        <div>
          <div>End date <span class="italic text-sm text-red-700">*</span></div>
          <Calendar
            v-model="newBusinessModalData['end_date']"
            view="month"
            date-format="M-yy"
            class="h-10"
            :manual-input="false"
            @date-select="checkDates()"
          />
        </div>
      </div>
      <div
        v-if="newBusinessModalData['start_date'] && newBusinessModalData['end_date'] && isAllValidated[3] == 0"
        class="my-5 w-full p-8 bg-red-200 text-red-700 flex flex-row"
      >
        <div class="mr-2">
          <i class="pi pi-times-circle" style="font-size: 1.5rem"></i>
        </div>
        <div>Invalid Start Date or End Date</div>
      </div>
      <div class="flex flex-row mb-3">
        <div class="mr-2 w-1/2">
          <div>Attritional LR <span class="italic text-sm text-red-700">*</span></div>
          <div class="p-inputgroup h-10">
            <InputTextComponent
              v-model="newBusinessModalData['ATTRITIONAL']"
              :error="newBusinessModalData['ATTRITIONAL'] != '' && !isFinite(newBusinessModalData['ATTRITIONAL'])"
              @update:model-value="checkClaimsNature()"
            >
              <template #side>
                <span
                  class="inline-flex flex-none items-center justify-center w-12 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                  >%</span
                >
              </template>
            </InputTextComponent>
          </div>
        </div>

        <div class="w-1/2">
          <div>Elasticity <span class="italic text-sm text-red-700">*</span></div>
          <InputTextComponent
            v-model="newBusinessModalData['ELASTICITY']"
            :error="newBusinessModalData['ELASTICITY'] != '' && !isFinite(newBusinessModalData['ELASTICITY'])"
            class="h-10 w-full"
            @update:model-value="checkElasticity"
          >
            <template #side>
              <span
                class="inline-flex flex-none items-center justify-center w-12 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                >%</span
              >
            </template>
          </InputTextComponent>
        </div>
      </div>
      <div class="flex flex-row mb-3">
        <div v-for="(item, index) in parameters['claims_nature'].slice(1)" :key="index">
          <div>{{ formattedClaimsNature[index + 1] }} <span class="italic text-sm text-red-700">*</span></div>
          <div class="h-10 flex flex-row">
            <InputTextComponent
              v-model="newBusinessModalData[item]"
              :error="newBusinessModalData[item] != '' && !isFinite(newBusinessModalData[item])"
              @update:model-value="checkClaimsNature"
            >
              <template #side>
                <span
                  class="inline-flex flex-none items-center justify-center w-12 px-2 text-center border border-gray-400 bg-gray-100 text-sm"
                  >%</span
                >
              </template>
            </InputTextComponent>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end space-x-4 mt-4">
      <JetButton @click="onCloseModal">Cancel</JetButton>
      <JetButton type="primary" @click="configureEarningRatioDialog = true">Configure Earning Ratio</JetButton>
      <JetButton :disabled="!isAllValidated.every((e: any) => e == 1)" type="primary" @click="onConfirm"
        >Confirm</JetButton
      >
    </div>
  </DialogComponent>
</template>
